import { useRef, useEffect } from 'react';
import { ChevronDownIcon, SquaresPlusIcon } from '@heroicons/react/16/solid';
import SharedButton from '../../../../../../global/Button/Button';
import { ColumnVisibilityModels } from '../../../../../../components/Table/ColumnVisibilityModels';
import { SaveDropdown } from './SaveDropdown';
import { chemicalRestrictedColumns, defaultModel } from '../../../constants';
import { GridToolbarProps } from '../types';

export function GridToolbar({
  sourceWeights,
  showSaveDropdown,
  setShowSaveDropdown,
  onSaveComplete,
  onSaveColumnSelection,
  onSaveScore,
  isSaving,
  isAuthenticated,
  isAcademic,
  isTrial,
  view,
  tab,
  isEvidence,
  columnVisibilityModel,
  columnOrder,
  getFilterValueRef,
  searchItems,
  onDownload,
  showEnrichmentTrigger,
  enrichmentSelectionMode,
  onToggleEnrichmentSelection,
}: GridToolbarProps) {
  const saveDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (saveDropdownRef.current && !saveDropdownRef.current.contains(event.target as Node)) {
        setShowSaveDropdown(false);
      }
    };

    if (showSaveDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSaveDropdown, setShowSaveDropdown]);

  const getSaveDropdownProps = () => {
    const columnFilters = getFilterValueRef.current ? getFilterValueRef.current() : [];
    const contextParams = ['view', 'tab', 'source', 'idents'];
    const activeFilters = columnFilters.filter(
      (f: any) =>
        !contextParams.includes(f.name) &&
        f.value &&
        f.value !== '' &&
        !(Array.isArray(f.value) && f.value.length === 0),
    );
    const hasFilters = activeFilters.length > 0;
    const hasSearchItems = searchItems && searchItems.length > 0;

    const baseDefaultModel = ColumnVisibilityModels.getByViewTab(view, tab, isEvidence);

    const restrictChemical = isAuthenticated && isAcademic;
    const restrictedList = restrictChemical ? chemicalRestrictedColumns : [];

    const computedDefaultModel = defaultModel(baseDefaultModel, restrictedList);

    const hasCustomVisibility =
      columnVisibilityModel &&
      computedDefaultModel &&
      JSON.stringify(columnVisibilityModel) !== JSON.stringify(computedDefaultModel);

    const defaultColumnOrder = computedDefaultModel
      ? Object.keys(computedDefaultModel).filter(key => computedDefaultModel[key] === true)
      : [];

    const hasCustomOrder =
      columnOrder &&
      columnOrder.length > 0 &&
      JSON.stringify(columnOrder) !== JSON.stringify(defaultColumnOrder);

    const hasCustomColumns = hasCustomVisibility || hasCustomOrder;

    return {
      hasSearchItems,
      hasFilters,
      hasCustomColumns,
    };
  };

  const saveDropdownProps = getSaveDropdownProps();

  return (
    <div className="d-flex justify-content-end flex-wrap gap-2 align-items-center mb-4 md:mb-0">
      <div style={{ position: 'relative' }} ref={saveDropdownRef}>
        <SharedButton
          variant="secondaryOutline"
          onClick={() => setShowSaveDropdown(!showSaveDropdown)}
          disabled={(isSaving && !showEnrichmentTrigger) || (!isAuthenticated && !searchItems.length)}
          className="mt-2 mr-0"
        >
          <SquaresPlusIcon className="w-5 mr-2" />
          Actions
          <ChevronDownIcon
            className="w-4 ml-2 transition-transform duration-200"
            style={{ transform: showSaveDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </SharedButton>
        {showSaveDropdown && (
          <SaveDropdown
            {...saveDropdownProps}
            sourceWeights={sourceWeights}
            tab={tab}
            view={view}
            onDownload={ext => {
              onDownload(ext);
              setShowSaveDropdown(false);
            }}
            onSaveComplete={() => {
              onSaveComplete();
              setShowSaveDropdown(false);
            }}
            onSaveColumnSelection={() => {
              onSaveColumnSelection();
              setShowSaveDropdown(false);
            }}
            onSaveScore={() => {
              onSaveScore();
              setShowSaveDropdown(false);
            }}
            showEnrichmentTrigger={showEnrichmentTrigger}
            enrichmentSelectionMode={enrichmentSelectionMode}
            onEnrichmentAction={
              onToggleEnrichmentSelection
                ? () => {
                    onToggleEnrichmentSelection();
                    setShowSaveDropdown(false);
                  }
                : undefined
            }
            showSaveOptions={isAuthenticated && !isAcademic && !isTrial}
          />
        )}
      </div>
    </div>
  );
}
